<script setup>
import { ref, inject, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useObjectsStore } from '@/store/objects'

const dialogRef = inject('dialogRef')
const objectStore = useObjectsStore()

const router = useRouter()

const loading = ref(false)

const addColumns = ref([])
const dropColumns = ref([])
const addIndexes = ref([])
const dropIndexes = ref([])

const selectedTab = ref()
const currentObject = ref()

onMounted(() => {
  addColumns.value = [...(dialogRef.value.data.newFields)]
  addIndexes.value = [...(dialogRef.value.data.newIndices)]
  dropColumns.value = [...(dialogRef.value.data.fieldsToRemove)]
  dropIndexes.value = [...(dialogRef.value.data.indicesToRemove)]
  currentObject.value = dialogRef.value.data.currentObject

  selectedTab.value = defaultOpenTab()
})

const windowTooSmall = inject('windowTooSmall')

const hasChangedSomething = () => {
  return addColumns.value.length || dropColumns.value.length || addIndexes.value.length || dropIndexes.value.length
}

const defaultOpenTab = () => {
  if (addColumns.value.length) return '0'
  else if (dropColumns.value.length) return '1'
  else if (addIndexes.value.length) return '2'
  else return '3'
}

const saveChanges = async () => {
  loading.value = true
  disableModalClosure()
  const newCols = addColumns.value.map(el => el.apiName)
  const newIndices = addIndexes.value.map(el => el.apiName)
  try {
    await objectStore.saveMappingChangesToDB(currentObject.value.objectName, {
      addColumns: newCols,
      dropColumns: dropColumns.value.map(el => el.apiName),
      addIndexes: newIndices,
      dropIndexes: dropIndexes.value.map(el => el.apiName)
    })
  } catch (err) {
    console.error(err)
    // TODO: manage errors
  }
  router.push('/objects/')
  dialogRef.value.close()
}

const cancelDialog = () => {
  dialogRef.value.close()
}

const disableModalClosure = () => {
  dialogRef.value.options.props.closable = false
  dialogRef.value.options.props.closeOnEscape = false
}

</script>

<template>
  <div
    v-if="loading"
    class="flex flex-1 w-full items-center justify-center"
  >
    <ProgressSpinner />
  </div>
  <div
    v-else
    class="flex flex-col flex-grow pt-4 -mx-4 md:mx-0 rounded-lg bg-[--p-content-background] min-h-0"
  >
    <Tabs
      v-if="hasChangedSomething()"
      v-model:value="selectedTab"
      scrollable
      class="max-h-full flex-grow flex min-h-0"
    >
      <TabList class="top-0">
        <Tab
          v-if="addColumns.length"
          value="0"
        >
          <span v-if="!windowTooSmall"> Added Mappings ({{ addColumns.length }})</span>
          <span
            v-else
            class="flex items-center gap-2"
          ><i class="pi pi-plus text-green-500" /> Map. ({{ addColumns.length }})</span>
        </Tab>
        <Tab
          v-if="dropColumns.length"
          value="1"
        >
          <span v-if="!windowTooSmall"> Removed Mappings ({{ dropColumns.length }})</span>
          <span
            v-else
            class="flex items-center gap-2"
          ><i class="pi pi-times text-red-500" /> Map. ({{ dropColumns.length }})</span>
        </Tab>
        <Tab
          v-if="addIndexes.length"
          value="2"
        >
          <span v-if="!windowTooSmall"> Added Indices ({{ addIndexes.length }})</span>
          <span
            v-else
            class="flex items-center gap-2"
          ><i class="pi pi-plus text-green-500" /> Idx. ({{ addIndexes.length }})</span>
        </Tab>
        <Tab
          v-if="dropIndexes.length"
          value="3"
        >
          <span v-if="!windowTooSmall"> Removed Indices ({{ dropIndexes.length }})</span>
          <span
            v-else
            class="flex items-center gap-2"
          ><i class="pi pi-times text-red-500" /> Idx. ({{ dropIndexes.length }})</span>
        </Tab>
      </TabList>
      <TabPanels
        class="flex flex-grow min-h-0"
        style="padding-left: 0; padding-right: 0;"
      >
        <TabPanel
          value="0"
          class="w-full flex flex-col"
        >
          <DataTable
            v-if="selectedTab === '0'"
            :value="addColumns"
            scrollable
            scroll-height="flex"
            table-style="height: 100%; width: 100%; max-width: 100%;"
            :virtual-scroller-options="{ itemSize: 40, numToleratedItems: 50 }"
          >
            <Column
              field="apiName"
              header="API Name"
            />
            <Column
              v-if="!windowTooSmall"
              field="dataType"
              style="width: 150px"
              header="Data Type"
            />
          </DataTable>
        </TabPanel>
        <TabPanel
          value="1"
          class="w-full flex flex-col"
        >
          <DataTable
            v-if="selectedTab === '1'"
            :value="dropColumns"
            scrollable
            scroll-height="flex"
            table-style="height: 100%; width: 100%; max-width: 100%;"
            :virtual-scroller-options="{ itemSize: 40, numToleratedItems: 50 }"
          >
            <Column
              field="apiName"
              header="API Name"
            />
            <Column
              v-if="!windowTooSmall"
              field="dataType"
              style="width: 150px"
              header="Data Type"
            />
          </DataTable>
        </TabPanel>
        <TabPanel
          value="2"
          class="w-full flex flex-col"
        >
          <DataTable
            v-if="selectedTab === '2'"
            :value="addIndexes"
            scrollable
            scroll-height="flex"
            table-style="height: 100%; width: 100%; max-width: 100%;"
            :virtual-scroller-options="{ itemSize: 40, numToleratedItems: 50 }"
          >
            <Column
              field="apiName"
              header="API Name"
            />
            <Column
              v-if="!windowTooSmall"
              field="dataType"
              style="width: 150px"
              header="Data Type"
            />
          </DataTable>
        </TabPanel>
        <TabPanel
          value="3"
          class="w-full flex flex-col"
        >
          <DataTable
            v-if="selectedTab === '3'"
            :value="dropIndexes"
            scrollable
            scroll-height="flex"
            table-style="height: 100%; width: 100%; max-width: 100%;"
            :virtual-scroller-options="{ itemSize: 40, numToleratedItems: 50 }"
          >
            <Column
              field="apiName"
              header="API Name"
            />
            <Column
              v-if="!windowTooSmall"
              field="dataType"
              style="width: 150px"
              header="Data Type"
            />
          </DataTable>
        </TabPanel>
      </TabPanels>
    </Tabs>
    <p v-else>
      No Changes
    </p>
  </div>
  <div class="w-full flex justify-end gap-2 pt-4 border-t border-t-[#e0e3e8] dark:border-t-gray-600">
    <Button
      v-if="!loading"
      type="button"
      label="Cancel"
      severity="secondary"
      @click="cancelDialog"
    />
    <Button
      v-else
      type="button"
      label="Cancel"
      severity="secondary"
      disabled
    />
    <Button
      v-if="!loading"
      type="button"
      @click="saveChanges"
    >
      Save Changes
    </Button>
    <Button
      v-else
      type="button"
      disabled
    >
      Save Changes
    </Button>
  </div>
</template>

<style scoped>
  .p-datatable-scrollable-table>.p-datatable-tfoot {
    bottom: -1px
  }
</style>
